'use client';

// ============================================================================
// Diagnostic Modal — RMM-style support utility as full-screen modal
// ----------------------------------------------------------------------------
// Same functionality as diagnostics-auth page but as a modal overlay to avoid
// navigating users away from their current module context.
//
// USER DOCS: changes to this component should be accompanied by a
// corresponding update to the user guide:
// next_portal/user-docs/content/docs/getting-started/my-corner.mdx
// (published at user-docs.totalaccess.co.za/getting-started/my-corner#diagnostics)
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Cpu,
  Database,
  Download,
  Globe,
  HardDrive,
  Layers,
  Loader2,
  Lock,
  LogIn,
  MapPin,
  Minus,
  PauseCircle,
  Play,
  RefreshCw,
  Server,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Wifi,
  X,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import '@/app/professional-modern.css';
import {
  PROBE_REGISTRY,
  detectModuleContextWithOfflineFallback,
  detectRuntime,
  probeAuth,
  probeCameraHardware,
  probeDesktopBridge,
  probeEnvironment,
  probeGpsQuality,
  probeHardware,
  probeIndustrySolutions,
  probeModuleContext,
  probeNativeExtras,
  probeNetwork,
  probeOfflineQueue,
  probeRbac,
  probeStorage,
  probeWebSocketHealth,
} from '@/lib/diagnostics/probes';
import { autoCleanupLegacyStorage, cleanupLegacyStorage, hasLegacyStorage } from '@/lib/offline/storageCleanup';
import UserDocsLink from '@/components/shared/UserDocsLink';
import type {
  DiagnosticSnapshotPayload,
  ProbeId,
  ProbeResult,
  ProbeStatus,
  SnapshotRuntime,
} from '@/lib/diagnostics/types';

interface DiagnosticModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Probe metadata for the UI
// ============================================================================

const PROBE_ICONS: Record<ProbeId, React.ComponentType<{ className?: string }>> = {
  environment: Globe,
  hardware: Cpu,
  auth: ShieldCheck,
  'module-context': Layers,
  rbac: Lock,
  'industry-solutions': Activity,
  network: Wifi,
  storage: HardDrive,
  'native-extras': Smartphone,
  'desktop-bridge': Server,
  'camera-hardware': Camera,
  'gps-quality': MapPin,
  websockets: Share2,
  'offline-queue': Database,
};

const STATUS_PRESENTATION: Record<
  ProbeStatus,
  { label: string; tone: string; icon: React.ComponentType<{ className?: string }> }
> = {
  pending: { label: 'Pending', tone: 'text-gray-400 bg-gray-100 dark:bg-gray-700 dark:text-gray-300', icon: Minus },
  running: { label: 'Running', tone: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-300', icon: Loader2 },
  pass: { label: 'Healthy', tone: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300', icon: CheckCircle2 },
  warn: { label: 'Warning', tone: 'text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300', icon: AlertTriangle },
  fail: { label: 'Failed', tone: 'text-rose-700 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-300', icon: XCircle },
  skipped: { label: 'Skipped', tone: 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-300', icon: Minus },
};

interface ProbeRow extends ProbeResult {
  /** UI-only flag, never persisted */
  expanded?: boolean;
}

const initialiseRow = (id: ProbeId, label: string): ProbeRow => ({
  id,
  label,
  status: 'pending',
  summary: 'Waiting to run',
});

// ============================================================================
// Main modal component
// ============================================================================

export default function DiagnosticModal({ isOpen, onClose }: DiagnosticModalProps) {
  const { user, isAuthenticated } = useAuth();
  const { navigationData } = useNavigation();

  const [rows, setRows] = useState<ProbeRow[]>(() =>
    PROBE_REGISTRY.map(p => initialiseRow(p.id, p.label)),
  );
  const [running, setRunning] = useState(false);
  const [completedAt, setCompletedAt] = useState<Date | null>(null);
  const [moduleContext, setModuleContext] = useState<{ module: string | null; route: string | null; source: string }>({
    module: null,
    route: null,
    source: 'unknown',
  });
  const [runtime, setRuntime] = useState<SnapshotRuntime>('unknown');
  const [progress, setProgress] = useState(0);
  const [currentProbeId, setCurrentProbeId] = useState<ProbeId | null>(null);
  const [userNote, setUserNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snapshotRef, setSnapshotRef] = useState<string | null>(null);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [canShare, setCanShare] = useState(false);
  const cancelledRef = useRef(false);
  const [legacyStorageCheck, setLegacyStorageCheck] = useState<{
    hasLegacyIndexedDB: string[];
    hasLegacyLocalStorage: string[];
  } | null>(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{
    success: boolean;
    indexedDbDeleted: string[];
    localStorageRemoved: string[];
    bytesFreed: number;
    errors: string[];
  } | null>(null);
  const [cleanupCompletedThisSession, setCleanupCompletedThisSession] = useState(false);

  // ---------------------------------------------------------------- helpers --

  const updateRow = useCallback((id: ProbeId, patch: Partial<ProbeRow>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  // -------------------------------------------------------------- run probes --

  type ProbeRunner = () => Promise<ProbeResult>;

  const buildRunners = useCallback(
    (rt: SnapshotRuntime, detected: { module: string | null }, userId: number | string | null): Array<{ id: ProbeId; run: ProbeRunner }> => [
      { id: 'environment', run: () => probeEnvironment() },
      { id: 'hardware', run: () => probeHardware() },
      { id: 'auth', run: () => probeAuth() },
      { id: 'module-context', run: () => probeModuleContext() },
      { id: 'rbac', run: () => probeRbac(userId) },
      { id: 'industry-solutions', run: () => probeIndustrySolutions(detected.module, detected as { active_industry_bundle?: string | null }) },
      { id: 'network', run: () => probeNetwork() },
      { id: 'storage', run: () => probeStorage() },
      { id: 'native-extras', run: () => probeNativeExtras(rt) },
      { id: 'desktop-bridge', run: () => probeDesktopBridge() },
      { id: 'camera-hardware', run: () => probeCameraHardware() },
      { id: 'gps-quality', run: () => probeGpsQuality() },
      { id: 'websockets', run: () => probeWebSocketHealth() },
      { id: 'offline-queue', run: () => probeOfflineQueue() },
    ],
    [],
  );

  const runProbes = useCallback(
    async (subset?: Set<ProbeId>) => {
      if (running) return;
      cancelledRef.current = false;
      setRunning(true);
      setSubmitError(null);
      setProgress(0);

      const detected = await detectModuleContextWithOfflineFallback();
      const rt = detectRuntime();
      setModuleContext(detected);
      setRuntime(rt);

      // Reset (or partially reset) rows
      setRows(prev =>
        prev.map(r =>
          !subset || subset.has(r.id)
            ? { ...r, status: 'pending', summary: 'Queued', evidence: undefined, error: undefined, durationMs: undefined }
            : r,
        ),
      );

      const userId = user?.id ?? null;
      const runners = buildRunners(rt, detected, userId).filter(r => !subset || subset.has(r.id));
      const total = runners.length;
      let completed = 0;

      for (const { id, run } of runners) {
        if (cancelledRef.current) {
          updateRow(id, { status: 'skipped', summary: 'Cancelled by user' });
          continue;
        }
        setCurrentProbeId(id);
        updateRow(id, { status: 'running', summary: 'Running…' });
        try {
          const result = await run();
          updateRow(id, { ...result });
        } catch (err) {
          updateRow(id, {
            status: 'fail',
            summary: 'Probe threw an unexpected error',
            error: err instanceof Error ? err.message : String(err),
          });
        }
        completed += 1;
        setProgress(Math.round((completed / total) * 100));
      }

      setCurrentProbeId(null);
      setRunning(false);
      setCompletedAt(new Date());
    },
    [running, user, buildRunners, updateRow],
  );

  const cancelRun = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const reRunFailed = useCallback(() => {
    const failed = new Set<ProbeId>();
    rows.forEach(r => {
      if (r.status === 'fail' || r.status === 'warn') failed.add(r.id);
    });
    if (failed.size === 0) return;
    void runProbes(failed);
  }, [rows, runProbes]);

  // ------------------------------------------------------- snapshot building --

  const overallStatus: ProbeStatus = useMemo(() => {
    if (rows.some(r => r.status === 'fail')) return 'fail';
    if (rows.some(r => r.status === 'warn')) return 'warn';
    if (rows.every(r => r.status === 'pending')) return 'pending';
    if (running) return 'running';
    return 'pass';
  }, [rows, running]);

  const snapshotPayload = useMemo<DiagnosticSnapshotPayload>(
    () => ({
      schema_version: 1,
      generated_at: (completedAt || new Date()).toISOString(),
      detected_module: moduleContext.module,
      detected_route: moduleContext.route,
      runtime,
      results: rows.map(({ expanded, ...r }) => r),
      user_note: userNote.trim() || undefined,
    }),
    [completedAt, moduleContext, runtime, rows, userNote],
  );

  const reportText = useMemo(() => {
    const userDisplayName = user?.email
      || user?.display_name
      || [user?.first_name, user?.last_name].filter(Boolean).join(' ')
      || 'unknown';
    const lines: string[] = [
      `# TotalAccess Diagnostic Snapshot`,
      `Generated: ${snapshotPayload.generated_at}`,
      `Runtime: ${snapshotPayload.runtime}`,
      `Detected module: ${snapshotPayload.detected_module || 'unknown'}`,
      `Detected route: ${snapshotPayload.detected_route || 'unknown'}`,
      `User: ${userDisplayName} (id=${user?.id ?? '?'})`,
      `Employee ID: ${user?.employee_id ?? 'unknown'}`,
      `App ID: ${user?.app_id ?? 'none'}`,
      `Client: ${navigationData?.client_info?.name || 'unknown'} (id=${user?.client_id ?? navigationData?.client_info?.id ?? '?'})`,
    ];
    // Surface build version mismatch at the top of the report for quick triage
    const envRow = rows.find(r => r.id === 'environment');
    const bv = envRow?.evidence?.build_version as
      | { is_stale?: boolean; client_git_commit?: string | null; server_git_commit?: string | null; client_build_timestamp?: string | null; server_build_timestamp?: string | null }
      | null;
    if (bv) {
      lines.push(`Client build: ${bv.client_git_commit ?? '?'} (${bv.client_build_timestamp ?? '?'})`);
      lines.push(`Server build: ${bv.server_git_commit ?? '?'} (${bv.server_build_timestamp ?? '?'})`);
      if (bv.is_stale) lines.push(`⚠ STALE BUILD — client is running an old version`);
    }
    // Surface native app update status at the top of the report for quick triage
    const nativeRow = rows.find(r => r.id === 'native-extras');
    const nu = nativeRow?.evidence?.native_update_check as
      | { update_available?: boolean; current_version?: string | null; latest_version?: string | null; versions_behind?: number | null; store_channel?: string | null; mandatory?: boolean; error?: string }
      | null;
    if (nu) {
      if (nu.update_available) {
        lines.push(`⚠ NATIVE APP UPDATE: v${nu.current_version ?? '?'} → v${nu.latest_version ?? '?'} (${nu.store_channel ?? 'store'})${nu.mandatory ? ' [MANDATORY]' : ''}`);
      } else if (nu.error) {
        lines.push(`Native update check failed: ${nu.error}`);
      } else {
        lines.push(`Native app: v${nu.current_version ?? '?'} (up to date)`);
      }
    }
    // Surface device/OS info for Huawei diagnostics
    const nativeDeviceRow = rows.find(r => r.id === 'native-extras');
    const dev = nativeDeviceRow?.evidence?.device as
      | { manufacturer?: string; model?: string; os_version?: string; webview_version?: string; operating_system?: string }
      | null;
    if (dev) {
      lines.push(`Device: ${dev.manufacturer ?? '?'} ${dev.model ?? '?'} · ${dev.operating_system ?? '?'} ${dev.os_version ?? '?'} · WebView ${dev.webview_version ?? '?'}`);
    }
    // Surface keyboard/viewport state for hardware keyboard diagnostics
    const hwRow = rows.find(r => r.id === 'hardware');
    const kb = hwRow?.evidence?.keyboard as
      | { hardware_keyboard_likely?: boolean; soft_keyboard_likely_active?: boolean; max_touch_points?: number; visual_viewport_height?: number | null; window_inner_height?: number | null; viewport_height_delta?: number | null }
      | null;
    if (kb) {
      if (kb.hardware_keyboard_likely) lines.push(`Keyboard: hardware keyboard likely (touch=${kb.max_touch_points ?? '?'}, vv=${kb.visual_viewport_height ?? '?'}, win=${kb.window_inner_height ?? '?'})`);
      else if (kb.soft_keyboard_likely_active) lines.push(`Keyboard: soft keyboard active (vv=${kb.visual_viewport_height ?? '?'}, win=${kb.window_inner_height ?? '?'}, delta=${kb.viewport_height_delta ?? '?'})`);
    }
    lines.push('');
    rows.forEach(r => {
      lines.push(`## ${r.label}  [${r.status.toUpperCase()}${r.durationMs != null ? ` · ${r.durationMs}ms` : ''}]`);
      lines.push(r.summary || '(no summary)');
      if (r.error) lines.push(`Error: ${r.error}`);
      if (r.evidence) {
        lines.push('Evidence:');
        lines.push(JSON.stringify(r.evidence, null, 2));
      }
      lines.push('');
    });
    if (snapshotRef) {
      lines.push(`Snapshot ref: ${snapshotRef}`);
    }
    if (userNote.trim()) {
      lines.push(`User note: ${userNote.trim()}`);
    }
    return lines.join('\n');
  }, [rows, snapshotPayload, user, navigationData, snapshotRef, userNote]);

  // -------------------------------------------------------------- side-effects --

  useEffect(() => {
    let active = true;

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      setCanShare(true);
    }
    setRuntime(detectRuntime());

    void (async () => {
      const detected = await detectModuleContextWithOfflineFallback();
      if (active) {
        setModuleContext(detected);
      }
    })();

    // Check for legacy storage on load and auto-cleanup if applicable
    void (async () => {
      const legacy = await hasLegacyStorage();
      if (active) {
        // Only set the check result if there's actually legacy storage
        if ((legacy.hasLegacyIndexedDB.length || 0) > 0 || (legacy.hasLegacyLocalStorage.length || 0) > 0) {
          setLegacyStorageCheck(legacy);
          // Only auto-cleanup if we haven't already done it this session
          if (!cleanupCompletedThisSession) {
            try {
              await autoCleanupLegacyStorage();
              setCleanupCompletedThisSession(true);
              // Re-check after cleanup
              const updatedLegacy = await hasLegacyStorage();
              // Only update if there's still legacy storage after cleanup
              if ((updatedLegacy.hasLegacyIndexedDB.length || 0) > 0 || (updatedLegacy.hasLegacyLocalStorage.length || 0) > 0) {
                setLegacyStorageCheck(updatedLegacy);
              } else {
                setLegacyStorageCheck(null);
              }
            } catch (err) {
              console.error('[DiagnosticModal] Auto-cleanup failed:', err);
            }
          }
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [cleanupCompletedThisSession]);

  // Auto-run probes when modal opens
  useEffect(() => {
    if (isOpen && !running && rows.every(r => r.status === 'pending')) {
      void runProbes();
    }
  }, [isOpen, running, rows, runProbes]);

  // ------------------------------------------------------------- submit handler --

  const submitSnapshot = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const tokens = authService.getTokens();
      const accessToken = tokens?.access_token || tokens?.accessToken;
      if (!accessToken) throw new Error('You must be signed in to submit a diagnostic snapshot.');

      // Use the combined endpoint that creates a support ticket under
      // client_id 1 (TotalAccess) AND stores the diagnostic snapshot under
      // client_id 1, linked to the ticket. This ensures the support team can
      // see both the ticket and the snapshot in their total-support queue.
      const res = await fetch('/api/v1/support/diagnostic-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(snapshotPayload),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `Submission failed (HTTP ${res.status})`);
      }
      const ref = data.data?.snapshot_ref as string;
      const tNum = data.data?.ticket_number as string | undefined;
      setSnapshotRef(ref);
      setTicketNumber(tNum ?? null);
      // Stash for the support portal to auto-attach to the next ticket
      try {
        localStorage.setItem(
          'support_diagnostic_pending_ref',
          JSON.stringify({ ref, generated_at: snapshotPayload.generated_at }),
        );
      } catch {
        /* ignore */
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }, [snapshotPayload]);

  // ------------------------------------------------------------------ exports --

  const copyReport = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      notify.success('Diagnostic report copied to clipboard');
    } catch {
      notify.error('Failed to copy report to clipboard');
    }
  }, [reportText]);

  const downloadReport = useCallback(
    (format: 'json' | 'txt') => {
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const blob = format === 'json'
        ? new Blob([JSON.stringify({ ...snapshotPayload, snapshot_ref: snapshotRef }, null, 2)], { type: 'application/json' })
        : new Blob([reportText], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `totalaccess-diagnostic-${ts}.${format}`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      // Give the browser time to start the download before revoking the blob URL.
      window.setTimeout(() => {
        if (a.parentNode) {
          document.body.removeChild(a);
        }
        URL.revokeObjectURL(url);
      }, 1000);
    },
    [reportText, snapshotPayload, snapshotRef],
  );

  const shareReport = useCallback(async () => {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
      notify.error('Sharing is not supported on this device');
      return;
    }

    const shareTitle = 'TotalAccess Diagnostic Snapshot';
    const file = new File([reportText], 'totalaccess-diagnostic-report.txt', { type: 'text/plain' });
    const shareData: ShareData = {
      title: shareTitle,
      text: reportText,
    };

    // Prefer sharing as a file so the report survives as an attachment in email / chat apps.
    if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
      shareData.files = [file];
    }

    try {
      await navigator.share(shareData);
      notify.success('Report shared');
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        // User closed the share sheet — no feedback needed.
        return;
      }
      console.error('[diagnostics] share failed:', err);
      notify.error('Share failed, report copied to clipboard');
      try {
        await navigator.clipboard.writeText(reportText);
      } catch {
        // ignore clipboard fallback failure
      }
    }
  }, [reportText]);

  const handleCleanupLegacyStorage = useCallback(async () => {
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const result = await cleanupLegacyStorage();
      setCleanupResult(result);
      setCleanupCompletedThisSession(true);
      // Re-check for legacy storage after cleanup
      const legacy = await hasLegacyStorage();
      // Only set if there's still legacy storage after cleanup
      if ((legacy.hasLegacyIndexedDB.length || 0) > 0 || (legacy.hasLegacyLocalStorage.length || 0) > 0) {
        setLegacyStorageCheck(legacy);
      } else {
        setLegacyStorageCheck(null);
      }
    } catch (err) {
      setCleanupResult({
        success: false,
        indexedDbDeleted: [],
        localStorageRemoved: [],
        bytesFreed: 0,
        errors: [err instanceof Error ? err.message : String(err)],
      });
    } finally {
      setCleanupRunning(false);
    }
  }, []);

  // ----------------------------------------------------------- guard rails --

  if (!isAuthenticated) {
    return null; // Don't show modal if not authenticated
  }

  if (!isOpen) return null;

  const allDone = !running && rows.every(r => r.status !== 'pending' && r.status !== 'running');
  const hasResults = rows.some(r => r.status !== 'pending');
  const failedOrWarnCount = rows.filter(r => r.status === 'fail' || r.status === 'warn').length;

  return (
    <div className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto" data-tour-id="diagnostic-modal" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {/* ----- Header ----- */}
        <div className="mb-3 sm:mb-6 px-2 relative">
          <div className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
              <Activity className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1">Diagnostic Collector</h1>
          </div>
          <div className="absolute right-2 top-0 flex items-center gap-2">
            <UserDocsLink
              docsPath="/getting-started/my-corner#diagnostics"
              label="User guide: Diagnostic Collector"
            />
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <main className="grid grid-cols-1 gap-2 px-2 md:grid-cols-2 sm:gap-4">
          {/* ----- Detected context ----- */}
          <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
            <ContextCard
              user={user}
              clientName={navigationData?.client_info?.name ?? null}
              clientId={user?.client_id ?? navigationData?.client_info?.id ?? null}
              clientRole={navigationData?.user_info?.client_role ?? null}
              moduleContext={moduleContext}
              runtime={runtime}
            />
          </div>

          {/* ----- Run controls ----- */}
          <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
            <RunControls
              running={running}
              progress={progress}
              currentLabel={
                currentProbeId
                  ? PROBE_REGISTRY.find(p => p.id === currentProbeId)?.label ?? null
                  : null
              }
              hasResults={hasResults}
              failedOrWarnCount={failedOrWarnCount}
              overallStatus={overallStatus}
              onStart={() => void runProbes()}
              onCancel={cancelRun}
              onReRunFailed={reRunFailed}
            />
          </div>

          {/* ----- Probe list ----- */}
          <section className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
            <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
              <Activity className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" />
              Diagnostic probes
            </h3>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map(row => (
                <ProbeRowItem
                  key={row.id}
                  row={row}
                  onToggle={() =>
                    setRows(prev =>
                      prev.map(r => (r.id === row.id ? { ...r, expanded: !r.expanded } : r)),
                    )
                  }
                />
              ))}
            </ul>
          </section>

          {/* ----- Triage summary ----- */}
          {allDone && hasResults && (
            <div className="md:col-span-2">
              <TriageSummary rows={rows} detectedModule={moduleContext.module} />
            </div>
          )}

          {/* ----- Legacy Storage Cleanup ----- */}
          {(legacyStorageCheck?.hasLegacyIndexedDB.length || 0) > 0 ||
            (legacyStorageCheck?.hasLegacyLocalStorage.length || 0) > 0 ? (
            <section className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
              <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
                <Database className="w-4 h-4 mr-1.5 text-amber-600 dark:text-amber-400" />
                Legacy Storage Cleanup
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Old IndexedDB databases and localStorage keys from previous storage migrations can be safely removed.
              </p>

              {!cleanupResult && legacyStorageCheck && (
                <div className="space-y-3">
                  {(legacyStorageCheck.hasLegacyIndexedDB.length || 0) > 0 && (
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                        Legacy IndexedDB databases ({legacyStorageCheck.hasLegacyIndexedDB.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {legacyStorageCheck.hasLegacyIndexedDB.map(db => (
                          <span key={db} className="px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs font-mono">
                            {db}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(legacyStorageCheck.hasLegacyLocalStorage.length || 0) > 0 && (
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                        Legacy localStorage keys ({legacyStorageCheck.hasLegacyLocalStorage.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {legacyStorageCheck.hasLegacyLocalStorage.slice(0, 10).map(key => (
                          <span key={key} className="px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs font-mono">
                            {key}
                          </span>
                        ))}
                        {legacyStorageCheck.hasLegacyLocalStorage.length > 10 && (
                          <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs">
                            +{legacyStorageCheck.hasLegacyLocalStorage.length - 10} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => void handleCleanupLegacyStorage()}
                    disabled={cleanupRunning}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {cleanupRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    {cleanupRunning ? 'Cleaning up...' : 'Clean up legacy storage'}
                  </button>
                </div>
              )}

              {cleanupResult && (
                <div className="space-y-3">
                  {cleanupResult.success ? (
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                            Cleanup completed successfully
                          </div>
                          <div className="mt-2 space-y-1 text-sm text-emerald-800 dark:text-emerald-300">
                            {cleanupResult.indexedDbDeleted.length > 0 && (
                              <div>• Deleted {cleanupResult.indexedDbDeleted.length} IndexedDB database(s)</div>
                            )}
                            {cleanupResult.localStorageRemoved.length > 0 && (
                              <div>• Removed {cleanupResult.localStorageRemoved.length} localStorage key(s)</div>
                            )}
                            {cleanupResult.bytesFreed > 0 && (
                              <div>• Freed ~{(cleanupResult.bytesFreed / 1024).toFixed(1)} KB from localStorage</div>
                            )}
                          </div>
                          <button
                            onClick={() => void handleCleanupLegacyStorage()}
                            disabled={cleanupRunning}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <RefreshCw className="h-3.5 w-3.5" /> Re-check
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-4">
                      <div className="flex items-start gap-3">
                        <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-rose-900 dark:text-rose-200">
                            Cleanup failed
                          </div>
                          {cleanupResult.errors.length > 0 && (
                            <ul className="mt-2 text-sm text-rose-800 dark:text-rose-300 list-disc list-inside">
                              {cleanupResult.errors.map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          )}
                          <button
                            onClick={() => void handleCleanupLegacyStorage()}
                            disabled={cleanupRunning}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-rose-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <RefreshCw className="h-3.5 w-3.5" /> Retry
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          ) : null}

          {/* ----- Submit & export ----- */}
          {allDone && hasResults && (
            <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
              <SubmitPanel
                snapshotRef={snapshotRef}
                ticketNumber={ticketNumber}
                submitting={submitting}
                submitError={submitError}
                userNote={userNote}
                onChangeNote={setUserNote}
                onSubmit={() => void submitSnapshot()}
                onCopy={() => void copyReport()}
                onDownloadJson={() => downloadReport('json')}
                onDownloadTxt={() => downloadReport('txt')}
                onShare={() => void shareReport()}
                canShare={canShare}
                overallStatus={overallStatus}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function ContextCard({
  user,
  clientName,
  clientId,
  clientRole,
  moduleContext,
  runtime,
}: {
  user: ReturnType<typeof useAuth>['user'];
  clientName: string | null;
  clientId: number | string | null;
  clientRole: string | null;
  moduleContext: { module: string | null; route: string | null; source: string };
  runtime: SnapshotRuntime;
}) {
  const userDisplayName = user?.email
    || user?.display_name
    || [user?.first_name, user?.last_name].filter(Boolean).join(' ')
    || 'unknown';
  const items: Array<{ label: string; value: string; icon: React.ComponentType<{ className?: string }> }> = [
    { label: 'User', value: `${userDisplayName} (id=${user?.id ?? '?'})`, icon: ShieldCheck },
    { label: 'Employee ID', value: user?.employee_id != null ? String(user.employee_id) : 'unknown', icon: ShieldCheck },
    { label: 'App ID', value: user?.app_id ?? 'none', icon: Smartphone },
    { label: 'Auth role', value: String(user?.role ?? 'unknown'), icon: Lock },
    { label: 'Client', value: clientName ? `${clientName} (id=${clientId ?? '?'})` : `id=${clientId ?? '?'}`, icon: Database },
    { label: 'Client role', value: clientRole ?? 'unknown', icon: ShieldCheck },
    {
      label: 'Detected module',
      value: moduleContext.module
        ? `${moduleContext.module} (via ${moduleContext.source})`
        : 'No prior module detected',
      icon: Layers,
    },
    { label: 'Runtime', value: runtime, icon: Server },
  ];

  return (
    <>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
        Detected context
      </h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
        {items.map(item => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className="flex items-start gap-2 p-2 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40"
            >
              <Icon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {item.label}
                </div>
                <div className="text-xs font-medium text-gray-800 dark:text-gray-100 break-words">
                  {item.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function RunControls({
  running,
  progress,
  currentLabel,
  hasResults,
  failedOrWarnCount,
  overallStatus,
  onStart,
  onCancel,
  onReRunFailed,
}: {
  running: boolean;
  progress: number;
  currentLabel: string | null;
  hasResults: boolean;
  failedOrWarnCount: number;
  overallStatus: ProbeStatus;
  onStart: () => void;
  onCancel: () => void;
  onReRunFailed: () => void;
}) {
  const overall = STATUS_PRESENTATION[overallStatus];
  const OverallIcon = overall.icon;
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${overall.tone}`}>
            <OverallIcon className={`w-5 h-5 ${running && overallStatus === 'running' ? 'animate-spin' : ''}`} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {running
                ? `Probing ${currentLabel ?? '…'}`
                : hasResults
                  ? `Last run · ${failedOrWarnCount === 0 ? 'all healthy' : `${failedOrWarnCount} item(s) need attention`}`
                  : 'Ready to start a new diagnostic run'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {running
                ? `${progress}% complete`
                : hasResults
                  ? `Overall status: ${overall.label}`
                  : 'Click Start to begin probing your environment'}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!running && (
            <button
              onClick={onStart}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {hasResults ? <RefreshCw className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {hasResults ? 'Re-run all' : 'Start diagnostic'}
            </button>
          )}
          {running && (
            <button
              onClick={onCancel}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <PauseCircle className="h-4 w-4" /> Cancel
            </button>
          )}
          {!running && failedOrWarnCount > 0 && (
            <button
              onClick={onReRunFailed}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-amber-800 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-600 dark:bg-slate-900 dark:text-amber-300 dark:hover:bg-amber-950/30"
            >
              <RefreshCw className="h-4 w-4" /> Re-run flagged
            </button>
          )}
        </div>
      </div>

      {running && (
        <div className="mt-4">
          <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-2 bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </>
  );
}

function ProbeRowItem({ row, onToggle }: { row: ProbeRow; onToggle: () => void }) {
  const Icon = PROBE_ICONS[row.id];
  // ... (rest of the code remains the same)
  const status = STATUS_PRESENTATION[row.status];
  const StatusIcon = status.icon;
  const expandable = row.evidence != null || !!row.error;
  const meta = PROBE_REGISTRY.find(p => p.id === row.id);
  const storageWriteTest = row.id === 'storage'
    ? (row.evidence?.offline_db_write_test as {
      attempted?: boolean;
      writable?: boolean;
      error?: string | null;
    } | undefined)
    : undefined;
  const showStorageWriteCallout = Boolean(storageWriteTest?.attempted);

  return (
    <li>
      <button
        type="button"
        onClick={expandable ? onToggle : undefined}
        className={`w-full flex items-start gap-3 p-4 text-left ${expandable ? 'hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer' : 'cursor-default'}`}
      >
        <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
          <Icon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{row.label}</span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.tone}`}
            >
              <StatusIcon className={`h-3 w-3 ${row.status === 'running' ? 'animate-spin' : ''}`} />
              {status.label}
            </span>
            {row.durationMs != null && (
              <span className="text-[11px] text-gray-400 dark:text-gray-500">{row.durationMs} ms</span>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{meta?.description}</div>
          <div className="text-sm text-gray-700 dark:text-gray-200 mt-1 break-words">{row.summary}</div>
          {showStorageWriteCallout && (
            <div
              className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${storageWriteTest?.writable
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'}`}
            >
              {storageWriteTest?.writable ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5" />
              )}
              {storageWriteTest?.writable ? 'Offline DB write test passed' : 'Offline DB write test failed'}
            </div>
          )}
          {showStorageWriteCallout && !storageWriteTest?.writable && storageWriteTest?.error && (
            <div className="mt-1 text-xs text-rose-600 dark:text-rose-400 break-words">
              {storageWriteTest.error}
            </div>
          )}
          {row.error && <div className="text-sm text-rose-600 dark:text-rose-400 mt-1">{row.error}</div>}
        </div>
        {expandable && (
          <div className="text-gray-400 mt-1">
            {row.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        )}
      </button>
      {expandable && row.expanded && (
        <div className="px-4 pb-4 -mt-1">
          <pre className="bg-gray-900 text-gray-100 text-[11px] leading-5 rounded-lg p-3 overflow-x-auto">
            {JSON.stringify(row.evidence ?? { error: row.error }, null, 2)}
          </pre>
        </div>
      )}
    </li>
  );
}

// ============================================================================
// Triage Summary — surfaces actionable findings for the support agent
// ============================================================================

interface Finding {
  severity: 'critical' | 'warning' | 'info';
  probe: ProbeId;
  title: string;
  detail: string;
}

const FIELD_MODULES = new Set(['total-operations', 'total-forms', 'work-tracker']);

function buildFindings(rows: ProbeRow[], detectedModule: string | null): Finding[] {
  const findings: Finding[] = [];
  const isFieldModule = detectedModule ? FIELD_MODULES.has(detectedModule) : false;

  for (const row of rows) {
    const ev = row.evidence;
    if (!ev) continue;

    switch (row.id) {
      case 'environment': {
        const buildVersion = ev.build_version as {
          is_stale?: boolean;
          client_git_commit?: string | null;
          server_git_commit?: string | null;
          client_build_timestamp?: string | null;
          server_build_timestamp?: string | null;
          fetch_error?: string | null;
        } | null;
        if (buildVersion?.is_stale) {
          findings.push({
            severity: 'warning',
            probe: 'environment',
            title: 'Stale build detected — user is running an old version',
            detail: `The client is running build ${buildVersion.client_git_commit?.slice(0, 7) ?? '?'} (built ${buildVersion.client_build_timestamp ?? '?'}) but the server is on build ${buildVersion.server_git_commit?.slice(0, 7) ?? '?'} (built ${buildVersion.server_build_timestamp ?? '?'}). The user should reload the app or clear the service worker cache to get the latest version. On PWA, closing and reopening the app should trigger an auto-update.`,
          });
        }
        break;
      }
      case 'hardware': {
        const kb = ev.keyboard as {
          hardware_keyboard_likely?: boolean;
          soft_keyboard_likely_active?: boolean;
          max_touch_points?: number;
          visual_viewport_height?: number | null;
          window_inner_height?: number | null;
          viewport_height_delta?: number | null;
          visual_viewport_offset_top?: number | null;
          visual_viewport_scale?: number | null;
          visual_viewport_supported?: boolean;
        } | null;
        if (kb?.hardware_keyboard_likely) {
          findings.push({
            severity: 'info',
            probe: 'hardware',
            title: 'Hardware keyboard detected',
            detail: `A physical/hardware keyboard appears to be connected (touch device with no soft keyboard active). visualViewport height: ${kb.visual_viewport_height ?? '?'}, window height: ${kb.window_inner_height ?? '?'}, delta: ${kb.viewport_height_delta ?? '?'}. If text inputs are invisible only when the hardware keyboard is active, this is likely a Huawei WebView rendering bug — check the WebView version in the Native Shell Details probe.`,
          });
        }
        if (kb && kb.visual_viewport_height != null && kb.window_inner_height != null && kb.viewport_height_delta != null) {
          if (kb.viewport_height_delta > 50) {
            findings.push({
              severity: 'info',
              probe: 'hardware',
              title: `Visual viewport is ${kb.viewport_height_delta}px shorter than window`,
              detail: `visualViewport.height (${kb.visual_viewport_height}) is significantly less than window.innerHeight (${kb.window_inner_height}). This usually means the soft keyboard is active, but if the user reports no soft keyboard, the WebView may be incorrectly resizing the visual viewport.`,
            });
          }
        }
        break;
      }
      case 'auth': {
        const expiresIn = ev.expires_in_sec as number | null;
        if (expiresIn != null && expiresIn < 1800) {
          const mins = Math.max(0, Math.round(expiresIn / 60));
          findings.push({
            severity: expiresIn < 300 ? 'critical' : 'warning',
            probe: 'auth',
            title: `Session expires in ${mins} minute${mins !== 1 ? 's' : ''}`,
            detail: 'Log out and back in before running further tests. Stale tokens cause false-positive access errors.',
          });
        }
        break;
      }
      case 'camera-hardware': {
        const perm = ev.permission_status as string | null;
        if (perm === 'denied') {
          findings.push({
            severity: isFieldModule ? 'critical' : 'warning',
            probe: 'camera-hardware',
            title: 'Camera access blocked',
            detail: isFieldModule
              ? 'Photo capture, vehicle inspections, and child entry forms all require camera access. Open browser site settings and allow the camera for this origin, then reload.'
              : 'Camera permission is blocked. Any feature requiring photo capture will be unavailable.',
          });
        } else if (perm === 'no_camera') {
          findings.push({
            severity: 'info',
            probe: 'camera-hardware',
            title: 'No camera device detected',
            detail: 'No video input device was found. Camera-dependent flows will fall back to file upload where supported.',
          });
        }
        break;
      }
      case 'gps-quality': {
        const accuracy = ev.accuracy_horizontal_m as number | null;
        const tier = ev.accuracy_tier as string | null;
        if (!ev.gps_supported) {
          findings.push({
            severity: isFieldModule ? 'warning' : 'info',
            probe: 'gps-quality',
            title: 'Geolocation not available',
            detail: 'The browser does not expose a GPS API. Location-tagged operations and geo-fenced attendance will not work.',
          });
        } else if (accuracy != null && (tier === 'poor' || tier === 'unusable')) {
          const isUnusable = tier === 'unusable';
          findings.push({
            severity: isFieldModule && isUnusable ? 'critical' : 'warning',
            probe: 'gps-quality',
            title: `${isUnusable ? 'Unusable' : 'Poor'} GPS accuracy (±${Math.round(accuracy)}m)`,
            detail: isFieldModule
              ? 'Accuracy below 50m is recommended for field job geo-tagging and site matching. On a browser, accuracy depends on Wi-Fi triangulation — enabling device GPS or moving outdoors improves this.'
              : 'GPS accuracy is low. Location-dependent features may produce imprecise results.',
          });
        }
        break;
      }
      case 'industry-solutions': {
        if (row.status === 'warn') {
          const bundle = ev.active_industry_bundle as string | null;
          const userAccess = ev.user_access as unknown[] | null;
          if (bundle && (!userAccess || userAccess.length === 0)) {
            findings.push({
              severity: 'warning',
              probe: 'industry-solutions',
              title: `Industry bundle mismatch: '${bundle}'`,
              detail: `The bundle '${bundle}' is recorded in your local storage, but the API returned no user-access records. Industry-specific features (kanban presets, custom job types, SLA rules) may not initialise correctly. Ask support to verify the bundle is activated for this client.`,
            });
          }
        }
        break;
      }
      case 'websockets': {
        const state = ev.connection_state as string | null;
        if (state && state !== 'connected') {
          findings.push({
            severity: 'critical',
            probe: 'websockets',
            title: `WebSocket is ${state}`,
            detail: 'Real-time updates (job assignments, notifications, live attendance) are not being received. Try refreshing. If the problem persists, check that wss://dev-websocket.totalaccess.co.za is reachable.',
          });
        }
        const reconnections = ev.reconnection_count as number | null;
        if (state === 'connected' && reconnections != null && reconnections > 2) {
          findings.push({
            severity: 'warning',
            probe: 'websockets',
            title: `WebSocket reconnected ${reconnections}× during this session`,
            detail: 'Repeated reconnections suggest intermittent network drops. Real-time events may have been missed during the gap.',
          });
        }
        break;
      }
      case 'offline-queue': {
        const failures = ev.sync_failures as number | null;
        const queueSize = ev.queue_size as number | null;
        if (failures && failures > 0) {
          findings.push({
            severity: 'warning',
            probe: 'offline-queue',
            title: `${failures} offline sync failure${failures !== 1 ? 's' : ''}`,
            detail: 'Queued items (photos, signatures, inspection forms) have failed to upload. Connect to a stable network and open the sync panel to retry.',
          });
        }
        if (queueSize && queueSize > 20 && (!failures || failures === 0)) {
          findings.push({
            severity: 'info',
            probe: 'offline-queue',
            title: `${queueSize} items pending upload`,
            detail: 'A large offline queue is queued. Ensure network connectivity so items sync before logging out.',
          });
        }
        break;
      }
      case 'network': {
        const latency = ev.api_latency_ms as number | null;
        const jitter = ev.api_latency_jitter_ms as number | null;
        const apiStatus = ev.api_status as number | null;
        const wsHandshake = ev.websocket_handshake as { outcome?: string; duration_ms?: number | null; error?: string | null } | null;
        const wsHttpHealth = ev.websocket_http_health as { attempted?: boolean; status?: number | null; ok?: boolean; error?: string | null } | null;
        if (apiStatus != null && !String(apiStatus).startsWith('2')) {
          findings.push({
            severity: 'critical',
            probe: 'network',
            title: `API health check returned HTTP ${apiStatus}`,
            detail: 'The API gateway may be down or experiencing issues. Contact TotalAccess support immediately.',
          });
        } else if (latency != null && latency > 800) {
          findings.push({
            severity: 'warning',
            probe: 'network',
            title: `High API latency (${latency}ms)`,
            detail: 'Slow responses may cause request timeouts or sluggish page loads. Check your network connection or VPN.',
          });
        }

        if (jitter != null && jitter > 400) {
          findings.push({
            severity: 'warning',
            probe: 'network',
            title: `Unstable API latency (jitter ${jitter}ms)`,
            detail: 'Latency swings suggest intermittent connectivity. Real-time and upload operations may behave inconsistently.',
          });
        }

        // WebSocket HTTP /health check — if the Go WS server itself is unreachable,
        // that's a critical finding (server down or network blocked).
        if (wsHttpHealth?.attempted && !wsHttpHealth.ok) {
          findings.push({
            severity: 'critical',
            probe: 'network',
            title: `WebSocket server /health check failed${wsHttpHealth.status != null ? ` (HTTP ${wsHttpHealth.status})` : ''}`,
            detail: wsHttpHealth.error
              ? `The Go WebSocket server health endpoint is unreachable: ${wsHttpHealth.error}. Real-time updates will not work until the server is reachable.`
              : 'The Go WebSocket server health endpoint returned a non-OK response. Real-time updates will not work until the server is healthy.',
          });
        }

        // WebSocket handshake — if HTTP /health is OK but the handshake fails, it's
        // likely an auth/token issue (warning), not a server-down issue (critical).
        if (wsHandshake?.outcome && wsHandshake.outcome !== 'connected') {
          const serverHealthy = wsHttpHealth?.attempted && wsHttpHealth.ok;
          findings.push({
            severity: serverHealthy ? 'warning' : 'critical',
            probe: 'network',
            title: `WebSocket handshake ${wsHandshake.outcome}`,
            detail: wsHandshake.error
              ? `The active handshake probe failed: ${wsHandshake.error}.${serverHealthy ? ' The server is reachable but the WebSocket upgrade was rejected — this may be an authentication issue. Try refreshing to get a fresh token.' : ' Real-time updates can be delayed or missed until connectivity stabilises.'}`
              : 'The active handshake probe did not establish a WebSocket connection. Real-time updates can be delayed or missed.',
          });
        }
        break;
      }
      case 'storage': {
        const criticalKeys = ev.critical_keys_present as Record<string, boolean> | null;
        if (criticalKeys) {
          const missing = Object.entries(criticalKeys).filter(([, v]) => !v).map(([k]) => k);
          if (missing.length > 0) {
            findings.push({
              severity: 'critical',
              probe: 'storage',
              title: `Missing critical storage keys: ${missing.join(', ')}`,
              detail: 'These keys are expected to be present after login. Their absence suggests a failed auth flow or cleared storage. Try logging out and back in.',
            });
          }
        }

        const offlineDbWriteTest = ev.offline_db_write_test as {
          attempted?: boolean;
          writable?: boolean;
          error?: string | null;
        } | null;
        if (offlineDbWriteTest?.attempted && !offlineDbWriteTest.writable) {
          findings.push({
            severity: 'critical',
            probe: 'storage',
            title: 'Offline database write test failed',
            detail: offlineDbWriteTest.error
              ? `IndexedDB writeability check failed: ${offlineDbWriteTest.error}. Offline prep and queue persistence may not work until browser/device storage is repaired.`
              : 'IndexedDB writeability check failed. Offline prep and queue persistence may not work until browser/device storage is repaired.',
          });
        }
        break;
      }
      case 'native-extras': {
        const nativeUpdate = ev.native_update_check as {
          update_available?: boolean;
          current_version?: string | null;
          latest_version?: string | null;
          versions_behind?: number | null;
          store_channel?: string | null;
          mandatory?: boolean;
          pending_update?: boolean;
          play_store_status?: string | null;
          error?: string;
        } | null;
        if (nativeUpdate?.error) {
          findings.push({
            severity: 'info',
            probe: 'native-extras',
            title: 'Native app update check failed',
            detail: `Could not verify the native app version against the server: ${nativeUpdate.error}. The support team can check manually via the deployment registry.`,
          });
        } else if (nativeUpdate?.update_available) {
          const storeName = nativeUpdate.store_channel === 'appgallery' ? 'AppGallery' : 'Play Store';
          const behind = nativeUpdate.versions_behind;
          findings.push({
            severity: nativeUpdate.mandatory ? 'critical' : 'warning',
            probe: 'native-extras',
            title: `Native app update available${behind && behind > 0 ? ` (${behind} release${behind === 1 ? '' : 's'} behind)` : ''}`,
            detail: `The installed native app is v${nativeUpdate.current_version ?? '?'} but the latest is v${nativeUpdate.latest_version ?? '?'}. The user should update via ${storeName}.${nativeUpdate.mandatory ? ' This is a mandatory update.' : ''}${nativeUpdate.pending_update ? ' Note: the update is pending and may not be live on the store yet.' : ''}`,
          });
        }
        // Check for old WebView version — Huawei WebView versions are notorious
        // for rendering bugs (especially around fixed-position modals and text inputs)
        const device = ev.device as {
          webview_version?: string;
          manufacturer?: string;
          model?: string;
          os_version?: string;
          operating_system?: string;
        } | null;
        const webviewVersion = device?.webview_version;
        if (webviewVersion) {
          const wvMajor = parseInt(webviewVersion.split('.')[0], 10);
          if (!Number.isNaN(wvMajor) && wvMajor < 83) {
            findings.push({
              severity: 'warning',
              probe: 'native-extras',
              title: `Old WebView version: ${webviewVersion}`,
              detail: `The device is running WebView ${webviewVersion}, which is below version 83. Old Huawei/EMUI WebView versions have known rendering bugs with fixed-position modals, text inputs, and the visualViewport API. The user should update the Android System WebView / Huawei WebView from the AppGallery. If the hardware keyboard issue only occurs on this device, the WebView version is the most likely culprit.`,
            });
          }
        }
        // Flag old Android/EMUI versions — EMUI is Huawei's Android fork and
        // older versions have well-documented rendering bugs with CSS
        // position:fixed, visualViewport, and hardware keyboard handling.
        // EMUI version mapping (approximate):
        //   EMUI 9  ≈ Android 9  (API 28)
        //   EMUI 10 ≈ Android 10 (API 29)
        //   EMUI 11 ≈ Android 10 (API 29)
        //   EMUI 12 ≈ Android 11 (API 30)
        //   HarmonyOS 3 ≈ Android 12 (API 31)
        const osVersion = device?.os_version;
        const manufacturer = device?.manufacturer?.toLowerCase() ?? '';
        const model = device?.model ?? '';
        if (osVersion && manufacturer.includes('huawei')) {
          const osMajor = parseInt(osVersion.split('.')[0], 10);
          if (!Number.isNaN(osMajor) && osMajor < 11) {
            findings.push({
              severity: 'warning',
              probe: 'native-extras',
              title: `Old Huawei/EMUI OS: Android ${osVersion} (${model})`,
              detail: `This Huawei device is running Android ${osVersion} (model: ${model}). Android versions below 11 on Huawei/EMUI have known rendering bugs with fixed-position modals and text inputs, especially when a hardware keyboard is connected. The visualViewport API is also unreliable on these versions. If the text input issue only occurs on this device, the OS/EMUI version is a strong contributing factor. The user should check for a system update in Settings → System & updates.`,
            });
          } else if (!Number.isNaN(osMajor) && osMajor < 12) {
            findings.push({
              severity: 'info',
              probe: 'native-extras',
              title: `Huawei device on Android ${osVersion} (${model})`,
              detail: `This Huawei device is running Android ${osVersion} (model: ${model}). Android 11 on Huawei/EMUI has some visualViewport quirks but is generally more reliable than older versions. If the text input issue occurs here, check the WebView version and whether a system update is available.`,
            });
          }
        }
        break;
      }
      default:
        break;
    }
  }

  return findings.sort((a, b) => {
    const order: Record<Finding['severity'], number> = { critical: 0, warning: 1, info: 2 };
    return order[a.severity] - order[b.severity];
  });
}

const SEVERITY_STYLES: Record<Finding['severity'], { bg: string; border: string; icon: React.ComponentType<{ className?: string }>; iconColor: string; titleColor: string; detailColor: string; badge: string }> = {
  critical: {
    bg: 'bg-rose-50 dark:bg-rose-900/15',
    border: 'border-rose-200 dark:border-rose-800',
    icon: XCircle,
    iconColor: 'text-rose-500 dark:text-rose-400',
    titleColor: 'text-rose-900 dark:text-rose-200',
    detailColor: 'text-rose-800 dark:text-rose-300',
    badge: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/15',
    border: 'border-amber-200 dark:border-amber-800',
    icon: AlertTriangle,
    iconColor: 'text-amber-500 dark:text-amber-400',
    titleColor: 'text-amber-900 dark:text-amber-200',
    detailColor: 'text-amber-800 dark:text-amber-300',
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/15',
    border: 'border-blue-200 dark:border-blue-800',
    icon: Sparkles,
    iconColor: 'text-blue-500 dark:text-blue-400',
    titleColor: 'text-blue-900 dark:text-blue-200',
    detailColor: 'text-blue-800 dark:text-blue-300',
    badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  },
};

function TriageSummary({ rows, detectedModule }: { rows: ProbeRow[]; detectedModule: string | null }) {
  const findings = useMemo(() => buildFindings(rows, detectedModule), [rows, detectedModule]);

  if (findings.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/15 px-4 py-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <div>
          <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">No issues found</div>
          <div className="text-xs text-emerald-800 dark:text-emerald-300">
            All probes passed. If you&apos;re still experiencing a problem, submit the snapshot and describe it in the notes.
          </div>
        </div>
      </div>
    );
  }

  const criticalCount = findings.filter(f => f.severity === 'critical').length;
  const warnCount = findings.filter(f => f.severity === 'warning').length;

  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Support Triage</h3>
        <div className="ml-auto flex items-center gap-1.5">
          {criticalCount > 0 && (
            <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">
              {criticalCount} critical
            </span>
          )}
          {warnCount > 0 && (
            <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
              {warnCount} warning
            </span>
          )}
        </div>
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {findings.map((finding, i) => {
          const styles = SEVERITY_STYLES[finding.severity];
          const Icon = styles.icon;
          const ProbeIcon = PROBE_ICONS[finding.probe];
          return (
            <li key={i} className={`p-4 ${styles.bg}`}>
              <div className="flex items-start gap-3">
                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${styles.iconColor}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-sm font-semibold ${styles.titleColor}`}>{finding.title}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles.badge}`}>
                      <ProbeIcon className="h-2.5 w-2.5" />
                      {PROBE_REGISTRY.find(p => p.id === finding.probe)?.label ?? finding.probe}
                    </span>
                  </div>
                  <p className={`text-xs leading-relaxed ${styles.detailColor}`}>{finding.detail}</p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function SubmitPanel({
  snapshotRef,
  ticketNumber,
  submitting,
  submitError,
  userNote,
  onChangeNote,
  onSubmit,
  onCopy,
  onDownloadJson,
  onDownloadTxt,
  onShare,
  canShare,
  overallStatus,
}: {
  snapshotRef: string | null;
  ticketNumber: string | null;
  submitting: boolean;
  submitError: string | null;
  userNote: string;
  onChangeNote: (value: string) => void;
  onSubmit: () => void;
  onCopy: () => void;
  onDownloadJson: () => void;
  onDownloadTxt: () => void;
  onShare: () => void;
  canShare: boolean;
  overallStatus: ProbeStatus;
}) {
  const overall = STATUS_PRESENTATION[overallStatus];

  return (
    <>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${overall.tone}`}>
          <Activity className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send to TotalAccess Support</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Optionally describe what you were trying to do, then submit. A support ticket will be
            created automatically for the TotalAccess team along with your diagnostic snapshot.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          Notes for support (optional)
        </label>
        <textarea
          value={userNote}
          onChange={e => onChangeNote(e.target.value)}
          rows={3}
          placeholder="What were you doing when this went wrong?"
          className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        />
      </div>

      {snapshotRef ? (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-300 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                Diagnostic submitted and support ticket created.
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-emerald-300 dark:border-emerald-700 font-mono text-base font-semibold text-emerald-900 dark:text-emerald-200">
                  {snapshotRef}
                </span>
                <button
                  onClick={() => void navigator.clipboard?.writeText(snapshotRef)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-emerald-800 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-700 dark:bg-slate-900 dark:text-emerald-200 dark:hover:bg-emerald-950/30"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy ref
                </button>
              </div>
              {ticketNumber && (
                <div className="mt-2 text-sm text-emerald-800 dark:text-emerald-300">
                  Support ticket created: <span className="font-mono font-semibold">{ticketNumber}</span>
                </div>
              )}
              <p className="text-xs text-emerald-800 dark:text-emerald-300 mt-2">
                The TotalAccess support team has been notified. Your ticket and diagnostic snapshot
                are now visible in the support queue.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {submitError && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3 text-sm text-rose-800 dark:text-rose-200">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>{submitError}</div>
            </div>
          )}
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Submitting…' : 'Submit to TotalAccess Support'}
          </button>
        </div>
      )}

      <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          Or export locally
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onCopy}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <Copy className="h-4 w-4" /> Copy report
          </button>
          <button
            onClick={onDownloadTxt}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <Download className="h-4 w-4" /> Download .txt
          </button>
          <button
            onClick={onDownloadJson}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <Download className="h-4 w-4" /> Download .json
          </button>
          <button
            onClick={onShare}
            disabled={!canShare}
            className={`inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs sm:text-sm font-semibold ${canShare
              ? 'border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
              : 'border border-gray-100 bg-white text-gray-300 cursor-not-allowed dark:border-gray-700 dark:bg-slate-900 dark:text-gray-600'
              }`}
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
        </div>
      </div>
    </>
  );
}
